<template>
  <!-- Cards on the Security tab; plain sections inside the Settings modal,
       where a card in a card loses its padding (AM-1106). -->
  <div>
    <component :is="parts.card">
      <component :is="parts.header" :class="headerClass">Restrictions</component>
      <component :is="parts.text">
        <!-- Toggles where cPanel has two states, and a radio group for outgoing
             mail, which has three: Hold does not fit on/off (AM-1104,
             2026-10-01). A switch reads "on" as allowed, the same way round as
             its label. -->
        <v-switch
          v-model="restrictions.incoming"
          color="green"
          class="mt-0"
          :true-value="ALLOW"
          :false-value="SUSPEND"
          :loading="pending.incoming"
          :disabled="busy"
          label="Receive incoming mail"
          hide-details
          @change="setRestriction('incoming', $event)"
        />

        <div class="mt-6">
          <div class="body-2 font-weight-medium">Sending outgoing email</div>
          <v-radio-group
            v-model="restrictions.outgoing"
            row
            class="mt-1"
            :disabled="busy"
            hide-details
            @change="setRestriction('outgoing', $event)"
          >
            <v-radio color="green" label="Allow" :value="ALLOW" />
            <v-radio color="green" label="Suspend" :value="SUSPEND" />
            <v-radio color="green" label="Hold" :value="HOLD" />
          </v-radio-group>
          <p class="body-2 text--secondary mt-2 mb-0">
            Suspend rejects outgoing mail. Hold queues it until sending is allowed again.
          </p>
        </div>

        <v-switch
          v-model="restrictions.login"
          color="green"
          class="mt-6"
          :true-value="ALLOW"
          :false-value="SUSPEND"
          :loading="pending.login"
          :disabled="busy"
          label="Allow logging in"
          hide-details
          @change="setRestriction('login', $event)"
        />
      </component>
    </component>

    <v-divider v-if="embedded" class="my-6"></v-divider>

    <component :is="parts.card" :class="{ 'mt-6': !embedded }">
      <component :is="parts.header" :class="headerClass">Plus Addressing</component>
      <component :is="parts.text">
        <gg-loading v-if="autocreate === null && pending.autocreate" :loading="true" type="list-item" />
        <p v-else-if="autocreate === null" class="body-2 mb-0">
          This setting isn't available right now.
        </p>
        <template v-else>
          <v-switch
            v-model="autocreate"
            color="green"
            class="mt-0"
            :loading="pending.autocreate"
            :disabled="pending.autocreate"
            label="Automatically create folders for plus addressing"
            hide-details
            @change="setAutocreate"
          />
          <p class="body-2 text--secondary mt-2 mb-0">
            Mail sent to {{ plusExample }} is filed into a folder named “folder”, created automatically if it doesn't exist.
          </p>
        </template>
      </component>
    </component>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { RESTRICTION_ALLOW, RESTRICTION_HOLD, RESTRICTION_SUSPEND } from '@/consts/email'

export default {
  name: 'MailboxSecurityView',
  props: {
    mailbox: {
      type: Object,
      required: true,
    },
    // Rendered inside a modal, which already supplies the card.
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      ALLOW: RESTRICTION_ALLOW,
      SUSPEND: RESTRICTION_SUSPEND,
      HOLD: RESTRICTION_HOLD,
      restrictions: { ...this.mailbox.restrictions },
      autocreate: null,
      pending: {
        incoming: false,
        outgoing: false,
        login: false,
        autocreate: false,
      },
    }
  },
  computed: {
    parts () {
      return this.embedded
        ? { card: 'section', header: 'h3', text: 'div' }
        : { card: 'gg-card', header: 'gg-card-header', text: 'gg-card-text' }
    },
    headerClass () {
      return this.embedded ? 'subtitle-1 font-weight-medium mb-3' : null
    },
    // One restriction at a time: each change is followed by a re-read of the
    // service, and two in flight would race each other's results.
    busy () {
      return this.pending.incoming || this.pending.outgoing || this.pending.login
    },
    plusExample () {
      return `${this.mailbox.user}+folder@${this.mailbox.domain}`
    },
  },
  methods: {
    ...mapActions('emailHub', ['setMailboxRestriction', 'loadMailboxAutocreate', 'setMailboxAutocreate']),
    ...mapActions(['reportError', 'reportSuccess']),
    /**
     * The control has already moved, so a failure puts it back to what the
     * mailbox last reported. On success `changed` re-reads the service, which
     * is what moves the status chip and the dashboard's row.
     */
    async setRestriction (kind, value) {
      const from = this.mailbox.restrictions[kind]
      if (value === from) return

      this.pending[kind] = true
      try {
        await this.setMailboxRestriction({
          serviceId: this.mailbox.serviceId,
          email: this.mailbox.email,
          kind,
          from,
          to: value,
        })
        this.reportSuccess('Mailbox restrictions updated.')
        this.$emit('changed')
      } catch (e) {
        this.restrictions[kind] = from
        this.reportError(e)
      } finally {
        this.pending[kind] = false
      }
    },
    async loadAutocreate () {
      this.pending.autocreate = true
      try {
        this.autocreate = await this.loadMailboxAutocreate({
          serviceId: this.mailbox.serviceId,
          email: this.mailbox.email,
        })
      } catch (e) {
        this.autocreate = null
      } finally {
        this.pending.autocreate = false
      }
    },
    async setAutocreate (enabled) {
      this.pending.autocreate = true
      try {
        await this.setMailboxAutocreate({
          serviceId: this.mailbox.serviceId,
          email: this.mailbox.email,
          enabled,
        })
        this.reportSuccess('Plus addressing updated.')
      } catch (e) {
        this.autocreate = !enabled
        this.reportError(e)
      } finally {
        this.pending.autocreate = false
      }
    },
  },
  watch: {
    // The account tab hands down a fresh object after every re-read.
    'mailbox.restrictions' (restrictions) {
      this.restrictions = { ...restrictions }
    },
  },
  created () {
    this.loadAutocreate()
  },
}
</script>
